import React from "react";

import {
  createOrdinaryTag,
  deployEdgeTags,
  fetchTagById,
  inspectEdgeTagCatalog,
  previewEdgeConnector,
  resolveTagPathToId,
} from "../../../api";
import { edgeConnectorTagEligibility } from "./edge-connector-spec";

export const KIND_LABELS = {
  gpio: "Built-in digital I/O",
  mqtt: "MQTT",
  modbus: "Modbus",
  modbus_tcp: "Modbus TCP",
  modbus_rtu: "Modbus RTU",
  "ncd-pr63-27c-usb": "Control Seat machine health sensor",
  "ncd-pr63-27c-ethernet": "Control Seat machine health sensor",
  opcua: "OPC UA",
  "enip-class1": "EtherNet/IP Class 1",
  ethercat: "EtherCAT",
  profinet: "PROFINET",
  fr202_analog: "FR202 built-in input",
};

const FLOW_LABELS = {
  observe: "Device updates tag",
  command: "Tag updates device",
  bidirectional: "Tag and device stay in sync",
  read: "Device updates tag",
  write: "Tag updates device",
  read_write: "Tag and device stay in sync",
};

const protocolAccessSummary = (kind, flow) => {
  const writes = ["command", "bidirectional", "write", "read_write"].includes(String(flow || ""));
  switch (kind) {
    case "opcua":
      return { label: "Access", value: writes ? "Read and write node" : "Monitor node" };
    case "mqtt":
      return { label: "MQTT action", value: writes ? "Publish tag changes" : "Subscribe to topic" };
    case "gpio":
    case "enip-class1":
    case "ethercat":
    case "profinet":
      return { label: "I/O type", value: writes ? "Digital output" : "Digital input" };
    case "fr202_analog":
      return { label: "I/O type", value: "Analog input" };
    case "modbus":
    case "modbus_tcp":
    case "modbus_rtu":
      return { label: "Access", value: writes ? "Read and write" : "Read only" };
    default:
      return { label: "Access", value: humanizeConnectorValue(flow, FLOW_LABELS) };
  }
};

const gpioBehaviorSummary = (kind, mapping) => {
  if (kind !== "gpio") return [];
  const native = mapping?.native_config || {};
  const writes = ["command", "bidirectional", "write", "read_write"].includes(String(mapping?.flow || ""));
  return [
    { label: "Signal polarity", value: native.active_low ? "Active low" : "Active high" },
    ...(writes ? [{ label: "Fallback output state", value: native.safe_state ? "On" : "Off" }] : []),
  ];
};

const decodeAddressPart = (value) => {
  try {
    return decodeURIComponent(String(value || "").replace(/\+/g, " "));
  } catch (_) {
    return String(value || "");
  }
};

export const humanPointAddress = (address, mapping = {}, kind = "") => {
  const raw = String(address || "");
  const bcm = raw.match(/^bcm:(\d+)$/);
  if (bcm) return `GPIO${bcm[1]}`;
  const dio = raw.match(/^dio:([1-3])$/);
  if (dio) return `DIO${dio[1]}`;
  const fr202 = raw.match(/^fr202:(AI[0-3]|RTD[0-1])$/);
  if (fr202) return fr202[1];
  const modbus = raw.match(/^fc([1-4]):(\d+):[^:]+(?::bit(\d+))?$/);
  if (modbus) {
    const labels = { 1: "Coil", 2: "Discrete input", 3: "Holding register", 4: "Input register" };
    return `${labels[modbus[1]]} ${modbus[2]}${modbus[3] == null ? "" : ` · bit ${modbus[3]}`}`;
  }
  const topic = raw.match(/(?:^|;)topic=([^;]*)/);
  if (topic) return `Topic ${decodeAddressPart(topic[1])}`;
  const node = raw.match(/(?:^|;)node_id=([^;]*)/);
  if (node) return `Node ${decodeAddressPart(node[1])}`;
  const native = mapping?.native_config || {};
  if (kind === "enip-class1" && native?.field?.bit_offset != null) return `DX${native.field.bit_offset}`;
  if (["ethercat", "profinet"].includes(kind) && native.channel != null) {
    return `${native.direction === "output" ? "Output" : "Input"} ${native.channel}`;
  }
  return raw;
};

export const humanizeConnectorValue = (value, labels) => {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  if (labels[raw]) return labels[raw];
  return raw.replace(/_/g, " ");
};

// Shared by every I/O workflow so creating a built-in channel and creating a
// protocol connection have the same one-review/one-confirm contract.
export function RevisionReview({ review, gatewayName, busy, busyLabel = "Applying…", canApply = true, onCancel, onConfirm }) {
  const preview = review?.preview;
  if (!preview) return null;
  const action = review?.action || "create";
  const title = action === "create"
    ? "Review connection"
    : action === "edit" ? "Review changes"
    : action === "start" ? "Turn this connection on?" : "Turn this connection off?";
  const confirmLabel = action === "create" ? "Connect" : action === "edit" ? "Save changes" : action === "start" ? "Turn on" : "Turn off";
  return (
    <div className="gwNotice gwConnectionReview" role="region" aria-label="Connection review">
      <div className="gwSectionTitle">{title}</div>
      <div className="gwConnectionReviewGrid">
        <div><span>Device</span><strong>{gatewayName || "Selected device"}</strong></div>
        <div><span>Protocol</span><strong>{humanizeConnectorValue(preview.kind, KIND_LABELS)}</strong></div>
        {action === "edit" ? <div><span>Connection state</span><strong>{preview.desired_state === "running" ? "On" : "Off"}</strong></div> : null}
      </div>
      {preview.sensor_receiver ? (
        <div className="gwConnectionReviewGrid">
          <div>
            <span>{preview.sensor_receiver.transport === "ethernet" ? "Ethernet receiver" : "USB receiver"}</span>
            <strong>{preview.sensor_receiver.port_label || "Detected receiver"}</strong>
          </div>
          <div><span>Data flow</span><strong>Receive only</strong></div>
          {preview.sensor_receiver.network_discovery ? (
            <div className="isWide"><span>Receiver address</span><strong>Found automatically on the local network</strong></div>
          ) : null}
          {preview.sensor_receiver.auto_discovery ? (
            <div className="isWide"><span>Sensor tags</span><strong>Created automatically when sensors report</strong></div>
          ) : null}
        </div>
      ) : null}
      {(preview.mappings || []).map((mapping) => {
        const access = protocolAccessSummary(preview.kind, mapping.flow);
        const behavior = gpioBehaviorSummary(preview.kind, mapping);
        return (
          <div className="gwConnectionReviewGrid" key={`${mapping.tag_path}:${mapping.native_address}`}>
            <div><span>Tag</span><strong>{mapping.tag_path}</strong></div>
            <div><span>Device point</span><strong>{humanPointAddress(mapping.native_address, mapping, preview.kind)}</strong></div>
            <div className="isWide"><span>{access.label}</span><strong>{access.value}</strong></div>
            {behavior.map((item) => (
              <div key={item.label}><span>{item.label}</span><strong>{item.value}</strong></div>
            ))}
          </div>
        );
      })}
      {review?.warning ? (
        <div className="gwAlert" role="alert">
          <div className="gwAlertTitle">{review.warning.title}</div>
          <div className="gwAlertBody">{review.warning.message}</div>
        </div>
      ) : null}
      <div className="gwHint">{busy ? busyLabel : `Nothing changes on ${gatewayName || "the device"} until you select ${confirmLabel}.`}</div>
      <div className="gwHeaderActions" style={{ marginTop: 10 }}>
        <button type="button" className="gwGhostBtn" disabled={busy} onClick={onCancel}>Cancel</button>
        <button type="button" className="gwPrimaryBtn" disabled={busy || !canApply} onClick={onConfirm}>
          {busy ? busyLabel : confirmLabel}
        </button>
      </div>
    </div>
  );
}

const pendingTagAPI = {
  createOrdinaryTag,
  deployEdgeTags,
  inspectEdgeTagCatalog,
  previewEdgeConnector,
  resolveTagPathToId,
  fetchTagById,
  wait: (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds)),
};

// A 422 from the connector endpoints is a validation rejection, and the server
// already redacted its body for operators (writeConnectorError runs it through
// RedactConnectorDiagnostic). Those messages name the exact conflicting device
// and setting — "this serial bus conflicts with X: … uses a different profile"
// — which no generic "check the settings and try again" can replace. Every
// other status keeps the caller's own mapped message.
export const connectorValidationMessage = (error) => {
  if (error?.status !== 422) return "";
  const text = String(error?.message || "")
    .trim()
    .replace(/^invalid connector configuration:\s*/i, "")
    .trim();
  if (!text) return "";
  const sentence = text.charAt(0).toUpperCase() + text.slice(1);
  return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
};

export async function preparePendingConnector({ gateway, pendingTag, pendingTags, spec, expectedRevision, onStatus, onCreated, api = pendingTagAPI }) {
  const tags = Array.isArray(pendingTags) ? pendingTags : pendingTag ? [pendingTag] : [];
  if (!tags.length) return api.previewEdgeConnector(gateway.id, spec, expectedRevision);
  for (let index = 0; index < tags.length; index += 1) {
    const tag = tags[index];
    if (tag.created) continue;
    onStatus(tags.length === 1 ? "Creating tag…" : `Creating tag ${index + 1} of ${tags.length}…`);
    // Tag creation is intentionally ordered. If a later tag fails, every
    // completed tag is recorded in review state so Retry never duplicates it.
    // eslint-disable-next-line no-await-in-loop
    try {
      await api.createOrdinaryTag({
        name: tag.name,
        path: tag.path,
        type: tag.type,
        writable: tag.writable,
        description: tag.description,
      });
    } catch (createError) {
      // A request can succeed on the server and lose its response. Before
      // telling the operator to retry a create that would now conflict,
      // resolve the exact path and accept only an identical ordinary tag.
      // A different type or writability remains a hard, visible conflict.
      const existingID = await api.resolveTagPathToId?.(tag.fullPath).catch(() => null);
      const existing = existingID ? await api.fetchTagById?.(existingID).catch(() => null) : null;
      const sameTag = existing && existing.fullPath === tag.fullPath &&
        existing.type === tag.type && Boolean(existing.writable) === Boolean(tag.writable);
      if (!sameTag) throw createError;
    }
    onCreated(tag.fullPath);
  }
  onStatus(`Preparing ${tags.length === 1 ? "tag" : "tags"} on ${gateway.name}…`);
  await api.deployEdgeTags(gateway.id, tags.map((tag) => ({
    path: tag.fullPath,
    name: tag.name,
    type: tag.type,
    writable: tag.writable,
  })));
  let failedTag = null;
  for (let attempt = 0; attempt < 20; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    await api.wait(1500);
    // eslint-disable-next-line no-await-in-loop
    const payload = await api.inspectEdgeTagCatalog(gateway.id, tags.map((tag) => tag.fullPath)).catch(() => null);
    failedTag = tags.map((tag) => ({
      tag,
      eligibility: payload ? edgeConnectorTagEligibility(payload, tag.fullPath, tag.direction) : null,
    })).find((candidate) => !candidate.eligibility?.eligible) || null;
    if (!failedTag) break;
  }
  if (failedTag) {
    const error = new Error("runtime tag confirmation failed");
    error.publicMessage = failedTag.eligibility?.message
      ? `${gateway.name} could not use ${failedTag.tag.fullPath}: ${failedTag.eligibility.message}`
      : `${gateway.name} has not confirmed ${failedTag.tag.fullPath} yet. Keep this review open and try Connect again.`;
    throw error;
  }
  onStatus("Preparing connection…");
  return api.previewEdgeConnector(gateway.id, spec, expectedRevision);
}
